import { environment } from '@/assets/environment';
import { StorageKey } from '@/enums/local-storage/storage-key.enum';
import { PayStatus } from '@/enums/orders/pay-status.enum';
import { CreateUserDeliveryAddress } from '@/models/client-orders/create-user-delivery-address.model';
import { UpdateUserDeliveryAddress } from '@/models/client-orders/update-user-delivery-address.model';
import { ExcludeStringIncomingEntity } from '@/models/entities/exclude-str-incoming-ent.model';
import { GoodType } from '@/models/entities/good-type.model';
import { IErrorResponse } from '@/models/errors/error-response.model';
import { DecodedToken } from '@/models/store/auth/decoded-token.model';
import { IClientUser } from '@/models/store/client-orders/client/client-user.model';
import { UpdateClientUser } from '@/models/store/client-orders/client/update-client-user.model';
import { IUserDeliveryAddress } from '@/models/store/client-orders/delivery-addresses/delivery-address.model';
import { IOrderFormData } from '@/models/store/client-orders/form-data/order-form-data.model';
import { IClientOrdersModule } from '@/models/store/client-orders/orders-module.model';
import { CreateOrder } from '@/models/store/client-orders/orders/create-order.model';
import { IOrder } from '@/models/store/client-orders/orders/order.model';
import { UpdateOrder } from '@/models/store/client-orders/orders/update-order.model';
import { storageService } from '@/services/storage';
import { isEqual } from 'lodash';
import { Module } from 'vuex';

const defaultOrder = {
    number: '',
    deliveryAddress: [],
    userId: '',
    email: '',
    orderStatus: 0,
    customerType: 0,
    payStatus: 0,
    payType: 0,
    deliveryType: 0,
    goods: [],
    personal: { email: '', phone: '', firstName: '', city: '' },
};

export const clientOrdersModule: Module<IClientOrdersModule, unknown> = {
    state: {
        defaultOrder,
        orders: [],
        bucketOrders: [],
        deliveryAddresses: [],
        client: {},
        orderFormData: {
            payTypes: [],
            customerTypes: [],
            orderStatuses: [],
            deliveryTypes: [],
            deliveryAddresses: [],
            freeDeliveryCities: [],
            bankDetails: { account: '', bankName: '', unp: '', bic: '' },
            termsOfAgreementLink: '',
        },
    },
    actions: {
        createOrder: async ({ commit }, payload: CreateOrder): Promise<IOrder | undefined> => {
            const token = storageService.getLocalStorageData(StorageKey.TOKEN);
            const headers = { 'x-app': 'secret_admin', 'Content-type': 'application/json' };

            if (token) Object.assign(headers, { authorization: token });

            const response = await fetch(`${environment.baseUrl}/api/client/orders`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                if (isEqual(response.status, 401)) commit('setUnAuthorized', true);

                if (Array.isArray(errorObject))
                    errorObject.forEach((item, idx: number) => {
                        if (idx)
                            setTimeout(
                                () =>
                                    commit('setSnackbarState', {
                                        status: true,
                                        text: item.description,
                                        isError: true,
                                    }),
                                1000,
                            );
                        else
                            commit('setSnackbarState', {
                                status: true,
                                text: item.description,
                                isError: true,
                            });
                    });

                return;
            }

            const data = await response.json();
            commit('setBucketOrders', [data]);

            return data;
        },
        updateOrder: async ({ commit }, payload: UpdateOrder): Promise<IOrder | undefined> => {
            const token = storageService.getLocalStorageData(StorageKey.TOKEN);
            const headers = { 'x-app': 'secret_admin', 'Content-type': 'application/json' };

            if (token) Object.assign(headers, { authorization: token });

            const response = await fetch(`${environment.baseUrl}/api/client/orders`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();

                if (isEqual(response.status, 401)) commit('setUnAuthorized', true);
                if ([500, 429].includes(response.status))
                    commit('setSnackbarState', {
                        status: true,
                        text: errorObject.error,
                        isError: true,
                    });

                if (Array.isArray(errorObject))
                    errorObject.forEach((item, idx: number) => {
                        if (idx)
                            setTimeout(
                                () =>
                                    commit('setSnackbarState', {
                                        status: true,
                                        text: item.description,
                                        isError: true,
                                    }),
                                1000,
                            );
                        else
                            commit('setSnackbarState', {
                                status: true,
                                text: item.description,
                                isError: true,
                            });
                    });

                return;
            }

            const data = await response.json();
            commit('setBucketOrders', [data]);

            return data;
        },
        getClientOrders: async (
            { commit },
            payload: { _id?: string },
        ): Promise<IOrder[] | IOrder> => {
            const decoded = storageService.decodeToken();
            const userId = storageService.getLocalStorageData(StorageKey.CLIENT);

            if (!(payload._id || (decoded as DecodedToken)?.email || userId)) {
                commit('setOrders', []);
                commit('setBucketOrders', []);

                return [];
            }

            const url = `api/client/order/${payload._id ? payload._id : '1'}`;

            const params = new URLSearchParams();
            if (userId) params.append('userId', `${userId}`);
            if ((decoded as DecodedToken)?.email)
                params.append('email', `${(decoded as DecodedToken).email}`);

            const response = await fetch(`${environment.baseUrl}/${url}?` + params, {
                method: 'get',
                headers: {
                    'x-app': 'secret_admin',
                    'Content-type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });

                return [];
            }

            const data = await response.json();
            data?.at(0)?.userId
                ? storageService.setLocalStorageData(StorageKey.CLIENT, data?.at(0)?.userId || '')
                : undefined;
            commit('setOrders', data);
            commit('setBucketOrders', data);

            return data;
        },
        fetchClientUser: async (
            { commit },
            payload: { _id?: string },
        ): Promise<IClientUser | undefined | null> => {
            const decoded = storageService.decodeToken();
            const token = storageService.getLocalStorageData(StorageKey.TOKEN);

            if (!payload._id && !decoded) return;

            const response = await fetch(
                `${environment.baseUrl}/api/client/user/${payload._id ? payload._id : (decoded as DecodedToken)._id}`,
                {
                    method: 'get',
                    headers: {
                        'x-app': 'secret_admin',
                        'Content-type': 'application/json',
                        Authorization: `${token}`,
                    },
                },
            );

            if (!response.ok) {
                if (isEqual(response.status, 401)) commit('setUnAuthorized', true);

                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });

                return;
            }

            const result = await response.json();

            if (result.data) {
                commit('setClientUser', result.data);
                storageService.setLocalStorageData(StorageKey.TOKEN, result.token);
            }

            return result.data;
        },
        createClient: async (): Promise<IClientUser> => {
            return {};
        },
        updateClientUser: async (
            { commit },
            payload: UpdateClientUser,
        ): Promise<IClientUser | undefined> => {
            const token = storageService.getLocalStorageData(StorageKey.TOKEN);
            const headers = { 'x-app': 'secret_admin', 'Content-type': 'application/json' };

            if (token) Object.assign(headers, { authorization: token });

            const response = await fetch(`${environment.baseUrl}/api/client/user`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();

                if (isEqual(response.status, 401)) commit('setUnAuthorized', true);

                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });

                return;
            }

            const result = await response.json();
            // if (result.data) commit('setClientUser', result.data);

            return result.data;
        },
        createDeliveryAddress: async (
            { commit },
            payload: CreateUserDeliveryAddress,
        ): Promise<IUserDeliveryAddress | undefined> => {
            const token = storageService.getLocalStorageData(StorageKey.TOKEN);
            const headers = { 'x-app': 'secret_admin', 'Content-type': 'application/json' };
            if (token) Object.assign(headers, { authorization: token });

            const response = await fetch(`${environment.baseUrl}/api/client/delivery-address`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });

                return;
            }

            const data = await response.json();
            commit('setDeliveryAddresses', [data]);

            return data;
        },
        updateDeliveryAddress: async (
            { commit },
            payload: UpdateUserDeliveryAddress,
        ): Promise<IUserDeliveryAddress | undefined> => {
            const token = storageService.getLocalStorageData(StorageKey.TOKEN);
            const headers = { 'x-app': 'secret_admin', 'Content-type': 'application/json' };
            if (token) Object.assign(headers, { authorization: token });

            const response = await fetch(`${environment.baseUrl}/api/client/delivery-address`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });

                return;
            }

            const data = await response.json();
            commit('setDeliveryAddresses', [data]);

            return data;
        },
        getUserDeliveryAddressById: async (
            { commit },
            payload: { userId: string },
        ): Promise<IUserDeliveryAddress[]> => {
            const userId = storageService.getLocalStorageData(StorageKey.CLIENT);

            const params = new URLSearchParams();
            if (userId) params.append('id', `${userId}`);

            const response = await fetch(
                `${environment.baseUrl}/api/client/delivery-address?` + params,
                {
                    method: 'get',
                    headers: {
                        'x-app': 'secret_admin',
                        'Content-type': 'application/json',
                    },
                },
            );

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });

                return [];
            }

            const data = await response.json();
            commit('setDeliveryAddresses', data);

            return data;
        },
        fetchGoodByCode: async (
            { commit },
            payload: { code: string; type: GoodType },
        ): Promise<ExcludeStringIncomingEntity | undefined> => {
            const params = new URLSearchParams();
            params.append('type', `${payload.type}`);

            const response = await fetch(
                `${environment.baseUrl}/api/client/goods/${payload.code}?` + params,
                {
                    method: 'get',
                    headers: {
                        'x-app': 'secret_admin',
                        'Content-type': 'application/json',
                    },
                },
            );

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });

                return;
            }

            return await response.json();
        },
        fetchOrderFormData: async ({ commit }): Promise<IOrderFormData | undefined> => {
            const response = await fetch(`${environment.baseUrl}/api/client/orders-form-data`, {
                method: 'get',
                headers: {
                    'x-app': 'secret_admin',
                    'Content-type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });

                return;
            }

            const data = await response.json();
            commit('setFormOrderData', data);

            return data;
        },
    },
    mutations: {
        setClientUser: (store, payload: IClientUser): void => {
            store.client = payload;
        },
        setOrders: (store, payload: IOrder[]): void => {
            store.orders = payload;
        },
        setBucketOrders: (store, payload: IOrder[]): void => {
            store.bucketOrders = payload.filter((item) =>
                isEqual(item.payStatus, PayStatus.IN_BUCKET),
            );
        },
        setDefaultOrder: (store, payload?: IOrder): void => {
            store.defaultOrder = payload ? { ...payload } : { ...defaultOrder };
            store.defaultOrder.goods = payload ? payload.goods : [];
        },
        setFormOrderData: (store, payload: IOrderFormData): void => {
            store.orderFormData = payload;
        },
        setDeliveryAddresses: (store, payload: IUserDeliveryAddress[]): void => {
            store.deliveryAddresses = store.deliveryAddresses.concat(payload);
        },
    },
    getters: {
        getOrders: (store) => store.orders,
        getBucketOrders: (store) => store.bucketOrders,
        getDeliveryAddresses: (store) => store.deliveryAddresses,
        getClient: (store) => store.client,
        getOrderFormData: (store) => store.orderFormData,
        getDefaultOrder: (store) => store.defaultOrder,
    },
};
